import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Award } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * TierProgressBar — Rewards Phase C.
 *
 * Visual language is a direct clone of the legacy passport
 * `RewardsCard` (the blue-gradient "your balance / current tier"
 * box).  Do NOT invent a new style here — we deliberately reuse
 * every class + inline-style value from `RewardsCard` so the V2
 * bar drops straight in where the V1 card used to sit and looks
 * pixel-identical to travelers.
 *
 * Content mapping vs V1:
 *   - Left column   :  V1 `balance` → V2 `lifetime_points`
 *   - Right column  :  V1 tier.name (e.g. "Bronze") → V2 current_tier
 *   - Below         :  same 3-column tier-progress row (curTier · pts
 *                       to next · nextTier) + gradient-filled bar
 *
 * Also polls unread `tier_upgraded` / `tier_corrected_by_admin`
 * notifications on mount and pops sonner toasts (sessionStorage
 * dedup, no auto-mark-read so bell badge still nudges).
 */
const TIER_COLORS = {
  Explorer:   "#8fa3b7",
  Adventurer: "#4a90a4",
  Navigator:  "#3a7d99",
  Pro:        "#c67e2e",
  Elite:      "#a83a3a",
};

const TIER_TOAST_TYPES = new Set(["tier_upgraded", "tier_corrected_by_admin"]);

async function popTierToastsForUnread() {
  try {
    const seenRaw = sessionStorage.getItem("tb:tierToastSeen") || "[]";
    const seen = new Set(JSON.parse(seenRaw));
    const { data } = await api.get("/notifications");
    const items = (data && data.notifications) || [];
    const fresh = items.filter(
      (n) => !n.read && TIER_TOAST_TYPES.has(n.type) && !seen.has(n.id),
    );
    if (!fresh.length) return;
    fresh.forEach((n) => seen.add(n.id));
    sessionStorage.setItem("tb:tierToastSeen", JSON.stringify([...seen]));
    fresh.forEach((n) => {
      const to = n.to_tier || "a new tier";
      if (n.type === "tier_upgraded") {
        toast.success(`You've reached the ${to} tier ✦`, { duration: 6000 });
      } else {
        const reason = n.reason ? ` — ${n.reason}` : "";
        toast(`Your tier was updated to ${to}${reason}`, { duration: 6000 });
      }
    });
  } catch {
    // Silent — notifications are best-effort UX polish.
  }
}

export default function TierProgressBar() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/rewards/v2/tier-status");
        if (!ignore) setStatus(data);
      } catch {
        if (!ignore) setError(true);
      }
      popTierToastsForUnread();
    })();
    return () => { ignore = true; };
  }, []);

  if (error || !status) return null;

  const curColor = TIER_COLORS[status.current_tier] || "#8fa3b7";
  const nxtColor = status.next_tier
    ? (TIER_COLORS[status.next_tier] || "#c67e2e")
    : curColor;
  const pct = Math.max(0, Math.min(100, status.progress_to_next?.percent ?? 100));
  const pointsToNext = status.progress_to_next?.points_to_next ?? 0;
  const lifetime = status.lifetime_points || 0;

  return (
    <div
      data-testid="tier-progress-bar"
      className="rounded-3xl border-2 border-tb-gold/60 p-5 text-tb-blue-soft"
      style={{
        background:
          "linear-gradient(135deg, #1a3a5f 0%, #2a4a72 55%, #1a3a5f 100%)",
      }}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="font-hand text-tb-blue text-base italic">
            your balance
          </div>
          <div
            data-testid="tier-progress-lifetime"
            className="font-serif text-white text-5xl leading-none mt-1"
          >
            {lifetime.toLocaleString()}
          </div>
          <div className="font-hand text-tb-blue mt-1">
            lifetime points <span className="text-tb-gold">✨</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-hand text-tb-blue text-sm italic">
            current tier
          </div>
          <div className="flex items-center justify-end gap-1.5 mt-0.5">
            <span
              data-testid="tier-progress-current"
              className="font-serif-it text-2xl"
              style={{ color: curColor }}
            >
              {status.current_tier}
            </span>
            <span className="text-tb-gold-bright">✦</span>
          </div>
          <Award
            className="w-6 h-6 ml-auto mt-1 text-tb-blue/70"
            strokeWidth={1.5}
          />
        </div>
      </div>

      {status.next_tier && (
        <div className="mt-5">
          <div className="flex items-end justify-between text-xs font-ui mb-1.5">
            <span style={{ color: curColor }}>{status.current_tier}</span>
            <span
              data-testid="tier-progress-to-next"
              className="font-hand text-tb-blue/80 italic"
            >
              {pointsToNext.toLocaleString()} pts to {status.next_tier}
            </span>
            <span
              data-testid="tier-progress-next"
              style={{ color: nxtColor }}
            >
              {status.next_tier}
            </span>
          </div>
          <div
            className="h-2 rounded-full bg-white/10 overflow-hidden"
            data-testid="tier-progress-track"
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full rounded-full"
              data-testid="tier-progress-fill"
              style={{
                background: `linear-gradient(90deg, ${curColor}, ${nxtColor})`,
              }}
            />
          </div>
        </div>
      )}

      {!status.next_tier && (
        <div className="mt-4 text-xs font-hand text-tb-blue/80 italic text-right"
             data-testid="tier-progress-max">
          top tier reached ✦
        </div>
      )}
    </div>
  );
}
